import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ForgotPassword, SetPassword } from './components/Account';
import { accentTones } from '@provalia/shared';
import { ApiError } from '../shared/api';
import { ErrorBox, Loading } from '../shared/ui';
import { api, type Me, type PortalInfo } from './api';
import { PortalContext, isBuyer, isStaff, type PortalContextValue } from './context';
import { Layout } from './components/Layout';
import { Login, Unavailable } from './components/Login';
import { Cartera } from './pages/Cartera';
import { Pedido } from './pages/Pedido';
import { Proveedores } from './pages/Proveedores';
import { Usuarios } from './pages/Usuarios';
import { Avisos } from './pages/Avisos';
import { Configuracion } from './pages/Configuracion';
import { Evaluacion } from './pages/Evaluacion';
import { Manual } from './pages/Manual';
import './portal.css';

/**
 * Portal de un cliente (<slug>.provalia.ecinsapp.es): lo usan su personal y
 * sus proveedores. El cliente lo decide el nombre de host, no el navegador.
 */
export function PortalApp(): JSX.Element {
  const [info, setInfo] = useState<PortalInfo | null>(null);
  const [infoError, setInfoError] = useState<unknown>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [checking, setChecking] = useState<boolean>(() => api.token() !== null);
  const [meError, setMeError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [forgot, setForgot] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Marca del cliente antes de entrar: color, título y logo.
  useEffect(() => {
    api
      .get<PortalInfo>('/portal/info')
      .then((i) => {
        setInfo(i);
        applyBrand(i.name, i.accentColor);
      })
      .catch(setInfoError);
  }, []);

  /** `background`: refresco con el portal ya abierto (p. ej. tras cambiar tolerancias); no tapa la pantalla. */
  const loadMe = useCallback(async (background = false) => {
    if (!api.token()) {
      setMe(null);
      setChecking(false);
      return;
    }
    if (!background) {
      setChecking(true);
      setMeError(null);
    }
    try {
      const m = await api.get<Me>('/portal/me');
      setMe(m);
      applyBrand(m.tenant.name, m.tenant.accentColor);
    } catch (err) {
      // 401 ya lo gestiona onUnauthorized; el resto se enseña con reintento.
      if (!background && !(err instanceof ApiError && err.status === 401)) setMeError(err);
    } finally {
      if (!background) setChecking(false);
    }
  }, []);

  useEffect(() => {
    api.onUnauthorized(() => {
      setMe(null);
      setNotice('Tu sesión ha caducado o ya no es válida. Vuelve a entrar.');
    });
    void loadMe();
  }, [loadMe]);

  const logout = useCallback(() => {
    api.clear();
    setMe(null);
    setNotice(null);
  }, []);

  const ctx = useMemo<PortalContextValue | null>(
    () => (me ? { me, reloadMe: () => loadMe(true), logout } : null),
    [me, loadMe, logout],
  );

  if (infoError) {
    const status = infoError instanceof ApiError ? infoError.status : 0;
    if (status === 404) return <Unavailable name={null} />;
    return (
      <div className="login-page">
        <div className="login-card">
          <ErrorBox error={infoError} onRetry={() => window.location.reload()} />
        </div>
      </div>
    );
  }
  if (!info) return <Loading label="Abriendo el portal…" />;
  if (!info.available) return <Unavailable name={info.name} />;
  if (checking) return <Loading label="Comprobando tu sesión…" />;
  if (meError) {
    return (
      <div className="login-page">
        <div className="login-card stack">
          <ErrorBox error={meError} onRetry={() => void loadMe()} />
          <button className="btn" onClick={logout}>
            Volver a la pantalla de entrada
          </button>
        </div>
      </div>
    );
  }
  // Enlaces de los correos: crear la contraseña (invitación) o elegir una nueva.
  const token = new URLSearchParams(location.search).get('token');
  if (token && (location.pathname === '/activar' || location.pathname === '/restablecer')) {
    return (
      <SetPassword
        info={info}
        token={token}
        onDone={(email) => {
          // La sesión que hubiera abierta ya no vale: se cerraron al cambiar la contraseña.
          api.clear();
          setMe(null);
          setLoginEmail(email);
          setNotice('Contraseña guardada. Ya puedes entrar.');
          navigate('/', { replace: true });
        }}
      />
    );
  }

  if (!ctx) {
    if (forgot) return <ForgotPassword info={info} onBack={() => setForgot(false)} />;
    return (
      <Login
        info={info}
        notice={notice}
        initialEmail={loginEmail}
        onForgot={() => setForgot(true)}
        onLogin={() => {
          setNotice(null);
          void loadMe();
        }}
      />
    );
  }

  const role = ctx.me.session.role;
  return (
    <PortalContext.Provider value={ctx}>
      <Layout>
        <Routes>
          <Route path="/" element={<Cartera />} />
          <Route path="/pedidos/:docEntry" element={<Pedido />} />
          <Route path="/evaluacion" element={<Evaluacion />} />
          {isStaff(role) ? <Route path="/proveedores" element={<Proveedores />} /> : null}
          {isBuyer(role) ? <Route path="/usuarios" element={<Usuarios />} /> : null}
          {isBuyer(role) ? <Route path="/avisos" element={<Avisos />} /> : null}
          {role === 'cliente_admin' ? <Route path="/configuracion" element={<Configuracion />} /> : null}
          {isStaff(role) ? <Route path="/manual" element={<Manual />} /> : null}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </PortalContext.Provider>
  );
}

function applyBrand(name: string, accent: string): void {
  if (/^#[0-9a-fA-F]{6}$/.test(accent)) {
    const tones = accentTones(accent);
    const root = document.documentElement.style;
    root.setProperty('--accent', accent);
    root.setProperty('--accent-ink', tones.ink);
    root.setProperty('--accent-text-light', tones.textLight);
    root.setProperty('--accent-text-dark', tones.textDark);
  }
  document.title = `${name} · Portal de proveedores`;
}

function NotFound(): JSX.Element {
  return (
    <div className="card">
      <div className="card-body empty">
        <div className="icon" aria-hidden="true">
          ?
        </div>
        <h2>Esta página no existe</h2>
        <p className="muted" style={{ margin: '8px 0 16px' }}>
          Puede que el enlace esté mal copiado o que no tengas acceso a esta sección.
        </p>
        <Link className="btn primary" to="/">
          Ir a la cartera de pedidos
        </Link>
      </div>
    </div>
  );
}
